import { ListChecks, Loader2, PanelRightClose, Check, X } from 'lucide-react'
import { useCallback, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { th } from 'date-fns/locale'

import { cn } from '@/common/lib/utils'
import { Button } from '@/common/ui/button'
import { ScrollArea } from '@/common/ui/scroll-area'

import {
	useQueryAdminTasks,
	useMutationAdminTaskDone,
	useMutationAdminTaskDismiss,
} from '../../../api/hooks'
import type { AdminTaskResponse } from '../../../api/services'

const MIN_PANEL_WIDTH = 280
const MAX_PANEL_WIDTH = 500
const DEFAULT_PANEL_WIDTH = 340

interface ChatAdminTasksPanelProps {
	roomId: string
	onClose: () => void
}

export function ChatAdminTasksPanel({
	roomId,
	onClose,
}: ChatAdminTasksPanelProps) {
	const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH)
	const [isDragging, setIsDragging] = useState(false)
	const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())

	const { data: tasks, isLoading } = useQueryAdminTasks(roomId)
	const doneMutation = useMutationAdminTaskDone(roomId)
	const dismissMutation = useMutationAdminTaskDismiss(roomId)

	const markPending = (id: string, isPending: boolean) => {
		setPendingIds((prev) => {
			const next = new Set(prev)
			if (isPending) next.add(id)
			else next.delete(id)
			return next
		})
	}

	const handleDone = async (task: AdminTaskResponse) => {
		markPending(task.id, true)
		try {
			await doneMutation.mutateAsync(task.id)
		} finally {
			markPending(task.id, false)
		}
	}

	const handleDismiss = async (task: AdminTaskResponse) => {
		markPending(task.id, true)
		try {
			await dismissMutation.mutateAsync(task.id)
		} finally {
			markPending(task.id, false)
		}
	}

	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault()
			setIsDragging(true)
			const startX = e.clientX
			const startWidth = panelWidth

			const handleMouseMove = (ev: MouseEvent) => {
				const deltaX = startX - ev.clientX
				const newWidth = Math.max(
					MIN_PANEL_WIDTH,
					Math.min(MAX_PANEL_WIDTH, startWidth + deltaX),
				)
				setPanelWidth(newWidth)
			}

			const handleMouseUp = () => {
				setIsDragging(false)
				document.removeEventListener('mousemove', handleMouseMove)
				document.removeEventListener('mouseup', handleMouseUp)
			}

			document.addEventListener('mousemove', handleMouseMove)
			document.addEventListener('mouseup', handleMouseUp)
		},
		[panelWidth],
	)

	const items = tasks ?? []

	return (
		<div className={cn('flex h-full', isDragging && 'select-none')}>
			<div
				onMouseDown={handleMouseDown}
				className={cn(
					'relative flex w-1 shrink-0 cursor-col-resize items-center justify-center bg-border transition-colors hover:bg-primary/20',
					isDragging && 'bg-primary/30',
				)}
			/>

			<div
				className="flex h-full flex-col border-l bg-background"
				style={{ width: panelWidth }}
			>
				<div className="flex items-center justify-between border-b px-4 py-3">
					<div className="flex items-center gap-2">
						<ListChecks className="h-5 w-5 text-amber-500" />
						<h3 className="font-semibold">แจ้งเตือน</h3>
						{items.length > 0 && (
							<span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900 dark:text-amber-200">
								{items.length}
							</span>
						)}
					</div>
					<Button
						variant="ghost"
						size="icon"
						className="h-8 w-8"
						onClick={onClose}
					>
						<PanelRightClose className="h-4 w-4" />
					</Button>
				</div>

				<div className="flex-1 overflow-hidden">
					{isLoading ? (
						<div className="flex h-full items-center justify-center">
							<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
						</div>
					) : items.length === 0 ? (
						<div className="flex h-full flex-col items-center justify-center px-6 text-center text-muted-foreground">
							<ListChecks className="mb-2 h-12 w-12 opacity-40" />
							<p className="text-sm font-medium">ไม่มีแจ้งเตือน</p>
							<p className="text-xs">
								AI
								จะเพิ่มแจ้งเตือนอัตโนมัติเมื่อมีข้อความใหม่ที่ต้องแอดมินจัดการ
							</p>
						</div>
					) : (
						<ScrollArea className="h-full">
							<div className="space-y-2 p-3">
								{items.map((task) => {
									const isPending = pendingIds.has(task.id)
									return (
										<div
											key={task.id}
											className="group rounded-lg border bg-card p-3 transition-colors hover:bg-accent/30"
										>
											<p className="mb-2 whitespace-pre-wrap text-sm leading-relaxed">
												{task.text}
											</p>
											<div className="flex items-center justify-between gap-2">
												<span className="text-[10px] text-muted-foreground">
													{formatDistanceToNow(new Date(task.createdAt), {
														addSuffix: true,
														locale: th,
													})}
												</span>
												<div className="flex gap-1">
													<Button
														variant="outline"
														size="sm"
														className="h-7 gap-1 border-emerald-500 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 dark:hover:bg-emerald-950"
														disabled={isPending}
														onClick={() => handleDone(task)}
													>
														{isPending ? (
															<Loader2 className="h-3 w-3 animate-spin" />
														) : (
															<Check className="h-3 w-3" />
														)}
														<span className="text-xs">ทำแล้ว</span>
													</Button>
													<Button
														variant="ghost"
														size="sm"
														className="h-7 gap-1 text-muted-foreground hover:bg-muted hover:text-foreground"
														disabled={isPending}
														onClick={() => handleDismiss(task)}
													>
														<X className="h-3 w-3" />
														<span className="text-xs">ไม่ทำ</span>
													</Button>
												</div>
											</div>
										</div>
									)
								})}
							</div>
						</ScrollArea>
					)}
				</div>
			</div>
		</div>
	)
}
